import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { NGROK } from "../../../../APIs";

import { Card, CardContent } from "../../../ui/common-ui/card";
import { Input } from "../../../ui/common-ui/input";
import { Badge } from "../../../ui/common-ui/badge";

import {
    ArrowLeft,
    Search,
    RefreshCw,
    Users,
    FolderKanban,
    Boxes,
    Shield,
    ExternalLink,
    X,
    Inbox,
    User,
    Eye,
} from "lucide-react";
import { FaGitlab } from "react-icons/fa6";
import { Button } from "../../../ui/common-ui/button";
import GitlabEntityAnalytics from "./GitlabEntityAnalytics";

type TenantData = any;

type GitlabUser = {
    id: number;
    tenantName: string;
    instanceUrl: string;
    gitlabId: number;
    name: string;
    userName: string;
    state: string;
    locked: boolean;
    bot: boolean;
    webUrl: string;
    createdAt: string;
    updatedAt: string;
};

type GitlabGroup = {
    id: number;
    tenantName: string;
    instanceUrl: string;
    gitlabId: number;
    name: string;
    path: string;
    fullPath: string;
    webUrl: string;
    visibility: string;
    parentId: number | null;
};

type GitlabProject = {
    id: number;
    tenantName: string;
    instanceUrl: string;
    gitlabId: number;
    name: string;
    path: string;
    pathWithNamespace: string;
    webUrl: string;
    visibility: string;
    archived: boolean;
    namespaceId: number | null;
    namespaceFullPath: string | null;
};

type GitlabMembership = {
    id: number;
    tenantName: string;
    instanceUrl: string;
    scopeType: string;
    scopeGitlabId: number;
    userGitlabId: number;
    accessLevel: number;
    accessLevelName: string;
    expiresAt: string | null;
    createdAt: string;
    updatedAt: string;
};

export const fetchTenants = async (): Promise<TenantData[]> => {
    const response = await axios.get(`${NGROK}/api/get-all-domains`, {});
    return response.data || [];
};

export const fetchGitlabInstanceUrl = async (tenantName: string): Promise<string> => {
    const response = await axios.get(`${NGROK}/api/saas/gitlab/auth/instance`, {
        params: { tenantName },
    });
    return response.data?.instanceUrl || "";
};

export const fetchGitlabUsers = async (tenantName: string, instanceUrl: string) => {
    const response = await axios.get(`${NGROK}/api/saas/gitlab/data/users`, {
        params: { tenantName, instanceUrl },
    });
    return response.data || [];
};

export const fetchGitlabGroups = async (tenantName: string, instanceUrl: string) => {
    const response = await axios.get(`${NGROK}/api/saas/gitlab/data/groups`, {
        params: { tenantName, instanceUrl },
    });
    return response.data || [];
};

export const fetchGitlabProjects = async (tenantName: string, instanceUrl: string) => {
    const response = await axios.get(`${NGROK}/api/saas/gitlab/data/projects`, {
        params: { tenantName, instanceUrl },
    });
    return response.data || [];
};

export const fetchGitlabMemberships = async (tenantName: string, instanceUrl: string) => {
    const response = await axios.get(`${NGROK}/api/saas/gitlab/data/memberships`, {
        params: { tenantName, instanceUrl },
    });
    return response.data || [];
};

function safeText(v: any) {
    if (v === null || v === undefined) return "";
    return String(v);
}

function extractTenantName(t: any): string {
    return t?.tenantName || t?.name || t?.domain || t?.tenant || t?.subdomain || t?.id || "";
}

function hostFromUrl(u: string) {
    try {
        return new URL(u).host;
    } catch {
        return u?.replace(/^https?:\/\//i, "") || "";
    }
}

function formatDate(v: any) {
    const s = safeText(v);
    if (!s) return "-";
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleString();
}

const getActivityColor = (count: number) => {
    if (count < 10) return "bg-gradient-to-r from-yellow-400 to-yellow-500";
    if (count <= 50) return "bg-gradient-to-r from-orange-400 to-orange-500";
    return "bg-gradient-to-r from-orange-600 to-orange-700";
};

const getActivityIntensity = (count: number) => {
    return 0;
};

function ActivityBar({ count, className = "" }: { count: number; className?: string }) {
    const intensity = getActivityIntensity(count);
    const colorClass = getActivityColor(count);

    return (
        <div className={`relative h-2 bg-muted rounded-full overflow-hidden ${className}`}>
            <div
                className={`h-full ${colorClass} rounded-full transition-all`}
                style={{ width: `${intensity}%` }}
            />
        </div>
    );
}

const SECTION_ORDER: SectionKey[] = ["users", "projects", "memberships", "groups"];

const SECTION_CONFIG = {
    users: {
        icon: Users,
        label: "Users",
        gradient: "from-blue-500/15 to-blue-600/5",
        border: "border-l-blue-500",
        iconBg: "bg-blue-500/10",
        iconColor: "text-blue-600 dark:text-blue-400",
        activeBg: "bg-blue-500",
        activeRing: "ring-blue-500/20",
    },
    projects: {
        icon: Boxes,
        label: "Projects",
        gradient: "from-violet-500/15 to-violet-600/5",
        border: "border-l-violet-500",
        iconBg: "bg-violet-500/10",
        iconColor: "text-violet-600 dark:text-violet-400",
        activeBg: "bg-violet-500",
        activeRing: "ring-violet-500/20",
    },
    memberships: {
        icon: Shield,
        label: "Memberships",
        gradient: "from-amber-500/15 to-amber-600/5",
        border: "border-l-amber-500",
        iconBg: "bg-amber-500/10",
        iconColor: "text-amber-600 dark:text-amber-400",
        activeBg: "bg-amber-500",
        activeRing: "ring-amber-500/20",
    },
    groups: {
        icon: FolderKanban,
        label: "Groups",
        gradient: "from-emerald-500/15 to-emerald-600/5",
        border: "border-l-emerald-500",
        iconBg: "bg-emerald-500/10",
        iconColor: "text-emerald-600 dark:text-emerald-400",
        activeBg: "bg-emerald-500",
        activeRing: "ring-emerald-500/20",
    },
} as const;

type SectionKey = keyof typeof SECTION_CONFIG;

function SkeletonCard() {
    return (
        <div className="rounded-xl border border-border/40 p-4 space-y-3 animate-pulse">
            <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-muted" />
                <div className="flex-1 space-y-2">
                    <div className="h-4 w-1/3 rounded bg-muted" />
                    <div className="h-3 w-2/3 rounded bg-muted" />
                </div>
                <div className="h-8 w-16 rounded-md bg-muted" />
            </div>
        </div>
    );
}

function EmptyState({ section, query }: { section: string; query: string }) {
    return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted/60 flex items-center justify-center mb-4">
                <Inbox className="w-8 h-8 text-muted-foreground/50" />
            </div>
            <h3 className="text-sm font-semibold text-foreground mb-1">
                {query ? "No matching results" : `No ${section} found`}
            </h3>
            <p className="text-xs text-muted-foreground max-w-[280px]">
                {query ? `No ${section} match "${query}". Try a different search term.` : `There are no ${section} synced for this tenant yet.`}
            </p>
        </div>
    );
}

type SelectedEntity = {
    type: "user" | "project" | "group" | "membership";
    data: any;
    entityValue: string;
    displayName: string;
};

export default function GitlabData({ onBack }: { onBack: () => void }) {
    const [tenants, setTenants] = useState<TenantData[]>([]);
    const [tenantName, setTenantName] = useState<string>("");
    const [instanceUrl, setInstanceUrl] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);

    const [users, setUsers] = useState<GitlabUser[]>([]);
    const [groups, setGroups] = useState<GitlabGroup[]>([]);
    const [projects, setProjects] = useState<GitlabProject[]>([]);
    const [memberships, setMemberships] = useState<GitlabMembership[]>([]);

    const [activeSection, setActiveSection] = useState<SectionKey>("users");
    const [query, setQuery] = useState<string>("");

    // Analytics view state
    const [selectedEntity, setSelectedEntity] = useState<SelectedEntity | null>(null);

    const filteredUsers = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return users;
        return users.filter(
            (u) =>
                safeText(u.name).toLowerCase().includes(q) ||
                safeText(u.userName).toLowerCase().includes(q) ||
                safeText(u.state).toLowerCase().includes(q) ||
                safeText(u.gitlabId).toLowerCase().includes(q)
        );
    }, [users, query]);

    const filteredGroups = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return groups;
        return groups.filter(
            (g) =>
                safeText(g.name).toLowerCase().includes(q) ||
                safeText(g.fullPath).toLowerCase().includes(q) ||
                safeText(g.visibility).toLowerCase().includes(q) ||
                safeText(g.gitlabId).toLowerCase().includes(q)
        );
    }, [groups, query]);

    const filteredProjects = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return projects;
        return projects.filter(
            (p) =>
                safeText(p.name).toLowerCase().includes(q) ||
                safeText(p.pathWithNamespace).toLowerCase().includes(q) ||
                safeText(p.visibility).toLowerCase().includes(q) ||
                safeText(p.gitlabId).toLowerCase().includes(q)
        );
    }, [projects, query]);

    const filteredMemberships = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return memberships;
        return memberships.filter(
            (m) =>
                safeText(m.scopeType).toLowerCase().includes(q) ||
                safeText(m.accessLevelName).toLowerCase().includes(q) ||
                safeText(m.userGitlabId).toLowerCase().includes(q) ||
                safeText(m.scopeGitlabId).toLowerCase().includes(q)
        );
    }, [memberships, query]);

    const rowsForSection = () => {
        if (activeSection === "users") return filteredUsers;
        if (activeSection === "groups") return filteredGroups;
        if (activeSection === "projects") return filteredProjects;
        return filteredMemberships;
    };

    const counts: Record<SectionKey, number> = {
        users: users.length,
        projects: projects.length,
        memberships: memberships.length,
        groups: groups.length,
    };

    const loadAll = async (tn: string) => {
        setLoading(true);
        try {
            const uri = await fetchGitlabInstanceUrl(tn);
            setInstanceUrl(uri || "");

            if (!uri) {
                setUsers([]);
                setGroups([]);
                setProjects([]);
                setMemberships([]);
                return;
            }

            const [u, g, p, m] = await Promise.all([
                fetchGitlabUsers(tn, uri),
                fetchGitlabGroups(tn, uri),
                fetchGitlabProjects(tn, uri),
                fetchGitlabMemberships(tn, uri),
            ]);

            setUsers(u || []);
            setGroups(g || []);
            setProjects(p || []);
            setMemberships(m || []);
        } catch {
            setUsers([]);
            setGroups([]);
            setProjects([]);
            setMemberships([]);
            setInstanceUrl("");
        } finally {
            setLoading(false);
        }
    };

    const init = async () => {
        const t = await fetchTenants();
        setTenants(t || []);
        const first = (t || []).map(extractTenantName).find(Boolean) || "";
        setTenantName(first);
        if (first) await loadAll(first);
    };

    useEffect(() => {
        init();
    }, []);

    const changeTenant = async (tn: string) => {
        setTenantName(tn);
        setQuery("");
        setSelectedEntity(null);
        await loadAll(tn);
    };

    const refresh = async () => {
        if (!tenantName) return;
        await loadAll(tenantName);
    };

    const openLink = (url: string) => {
        if (!url) return;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const handleCardClick = (row: any) => {
        if (activeSection === "users") {
            const u = row as GitlabUser;
            setSelectedEntity({
                type: "user",
                data: u,
                entityValue: `USER:${u.gitlabId}`,
                displayName: u.name || u.userName || String(u.gitlabId),
            });
        } else if (activeSection === "projects") {
            const p = row as GitlabProject;
            setSelectedEntity({
                type: "project",
                data: p,
                entityValue: `PROJECT:${p.gitlabId}`,
                displayName: p.name || p.pathWithNamespace || String(p.gitlabId),
            });
        } else if (activeSection === "groups") {
            const g = row as GitlabGroup;
            setSelectedEntity({
                type: "group",
                data: g,
                entityValue: `GROUP:${g.gitlabId}`,
                displayName: g.name || g.fullPath || String(g.gitlabId),
            });
        } else if (activeSection === "memberships") {
            const m = row as GitlabMembership;
            setSelectedEntity({
                type: "membership",
                data: m,
                entityValue: `MEMBERSHIP:${m.scopeType}:${m.scopeGitlabId}`,
                displayName: `${m.accessLevelName} · ${m.scopeType} #${m.scopeGitlabId}`,
            });
        }
    };

    const cfg = SECTION_CONFIG[activeSection];

    // If an entity is selected, show the analytics page
    if (selectedEntity) {
        return (
            <GitlabEntityAnalytics
                tenantName={tenantName}
                instanceUrl={instanceUrl}
                endpointType={selectedEntity.type}
                entityValue={selectedEntity.entityValue}
                displayName={selectedEntity.displayName}
                entityData={selectedEntity.data}
                onBack={() => setSelectedEntity(null)}
            />
        );
    }

    const renderRowCard = (row: any, index: number) => {
        const key = `${activeSection}:${row.id}`;

        if (activeSection === "users") {
            const u = row as GitlabUser;
            const status = safeText(u.state) || "unknown";
            const isActive = status === "active";
            const activity = u.gitlabId % 100;

            return (
                <Card
                    key={key}
                    className="hover-elevate cursor-pointer transition-transform hover:scale-[1.01]"
                    onClick={() => handleCardClick(u)}
                >
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <User className="w-4 h-4 text-primary" />
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                    <h3 className="font-semibold text-sm text-foreground truncate">{u.name || "-"}</h3>
                                    <Badge
                                        variant={isActive ? "default" : "secondary"}
                                        className={`text-xs h-5 ${!isActive ? "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200" : ""}`}
                                    >
                                        {isActive ? "Active" : status}
                                    </Badge>
                                    {u.bot && <Badge variant="secondary" className="text-[10px] h-5 font-medium">bot</Badge>}
                                    {u.locked && <Badge variant="secondary" className="text-[10px] h-5 font-medium">locked</Badge>}
                                </div>
                                <p className="text-xs text-muted-foreground truncate">
                                    @{u.userName || "-"} · ID {safeText(u.gitlabId) || "-"}
                                </p>
                            </div>

                            <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                <div className="text-xs text-muted-foreground">Activity</div>
                                <ActivityBar count={activity} className="w-20" />
                            </div>

                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                {u.webUrl && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                                        onClick={(e: any) => {
                                            e.stopPropagation();
                                            openLink(u.webUrl);
                                        }}
                                    >
                                        <ExternalLink className="w-3.5 h-3.5" />
                                    </Button>
                                )}
                                <Eye className="w-4 h-4 text-muted-foreground" />
                            </div>
                        </div>

                        {/* Info shown directly on card */}
                        <div className="mt-3 pt-3 border-t flex items-start gap-6">
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-muted-foreground mb-1.5">Created</div>
                                <div className="text-xs font-medium">{formatDate(u.createdAt)}</div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-muted-foreground mb-1.5">Updated</div>
                                <div className="text-xs font-medium">{formatDate(u.updatedAt)}</div>
                            </div>
                            <div className="flex-shrink-0 text-right">
                                <div className="text-xs text-muted-foreground mb-1.5">Instance</div>
                                <div className="text-xs font-medium truncate max-w-[200px]">{hostFromUrl(u.instanceUrl) || "-"}</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            );
        }

        if (activeSection === "projects") {
            const p = row as GitlabProject;
            const activity = p.gitlabId % 100;

            return (
                <Card
                    key={key}
                    className="hover-elevate cursor-pointer transition-transform hover:scale-[1.01]"
                    onClick={() => handleCardClick(p)}
                >
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <Boxes className="w-4 h-4 text-primary" />
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                    <h3 className="font-semibold text-sm text-foreground truncate">{p.name || "-"}</h3>
                                    <Badge variant="outline" className="text-[10px] h-5 font-medium">{p.visibility || "unknown"}</Badge>
                                    {p.archived && (
                                        <Badge variant="secondary" className="text-[10px] h-5 font-medium">archived</Badge>
                                    )}
                                </div>
                                <p className="text-xs text-muted-foreground truncate">
                                    {p.pathWithNamespace || "-"} · ID {safeText(p.gitlabId) || "-"}
                                </p>
                            </div>

                            <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                <div className="text-xs text-muted-foreground">Activity</div>
                                <ActivityBar count={activity} className="w-20" />
                            </div>

                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                {p.webUrl && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                                        onClick={(e: any) => {
                                            e.stopPropagation();
                                            openLink(p.webUrl);
                                        }}
                                    >
                                        <ExternalLink className="w-3.5 h-3.5" />
                                    </Button>
                                )}
                                <Eye className="w-4 h-4 text-muted-foreground" />
                            </div>
                        </div>

                        {/* Info shown directly on card */}
                        <div className="mt-3 pt-3 border-t flex items-start gap-6">
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-muted-foreground mb-1.5">Namespace</div>
                                <div className="text-xs font-medium truncate">{p.namespaceFullPath || "-"}</div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-muted-foreground mb-1.5">Path</div>
                                <div className="text-xs font-medium truncate">{p.pathWithNamespace || "-"}</div>
                            </div>
                            <div className="flex-shrink-0 text-right">
                                <div className="text-xs text-muted-foreground mb-1.5">Instance</div>
                                <div className="text-xs font-medium truncate max-w-[200px]">{hostFromUrl(p.instanceUrl) || "-"}</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            );
        }

        if (activeSection === "memberships") {
            const m = row as GitlabMembership;
            return (
                <Card
                    key={key}
                    className="hover-elevate cursor-pointer transition-transform hover:scale-[1.01]"
                    onClick={() => handleCardClick(m)}
                >
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <Shield className="w-4 h-4 text-primary" />
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                    <h3 className="font-semibold text-sm text-foreground truncate">{m.accessLevelName || "Membership"}</h3>
                                    <Badge variant="outline" className="text-[10px] h-5 font-medium">{m.scopeType || "scope"}</Badge>
                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400">
                                        Level {safeText(m.accessLevel)}
                                    </span>
                                </div>
                                <p className="text-xs text-muted-foreground truncate">
                                    Scope #{safeText(m.scopeGitlabId)} → User #{safeText(m.userGitlabId)}
                                </p>
                            </div>

                            <Eye className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        </div>

                        {/* Info shown directly on card */}
                        <div className="mt-3 pt-3 border-t flex items-start gap-6">
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-muted-foreground mb-1.5">Expires</div>
                                <div className="text-xs font-medium">{m.expiresAt ? formatDate(m.expiresAt) : "Never"}</div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-muted-foreground mb-1.5">Created</div>
                                <div className="text-xs font-medium">{formatDate(m.createdAt)}</div>
                            </div>
                            <div className="flex-shrink-0 text-right">
                                <div className="text-xs text-muted-foreground mb-1.5">Instance</div>
                                <div className="text-xs font-medium truncate max-w-[260px]">{hostFromUrl(m.instanceUrl) || "-"}</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            );
        }

        if (activeSection === "groups") {
            const g = row as GitlabGroup;
            return (
                <Card
                    key={key}
                    className="hover-elevate cursor-pointer transition-transform hover:scale-[1.01]"
                    onClick={() => handleCardClick(g)}
                >
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <FolderKanban className="w-4 h-4 text-primary" />
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                    <h3 className="font-semibold text-sm text-foreground truncate">{g.name || "-"}</h3>
                                    <Badge variant="outline" className="text-[10px] h-5 font-medium">{g.visibility || "unknown"}</Badge>
                                </div>
                                <p className="text-xs text-muted-foreground truncate">
                                    {g.fullPath || g.path || "-"} · ID {safeText(g.gitlabId) || "-"}
                                </p>
                            </div>

                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                {g.webUrl && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                                        onClick={(e: any) => {
                                            e.stopPropagation();
                                            openLink(g.webUrl);
                                        }}
                                    >
                                        <ExternalLink className="w-3.5 h-3.5" />
                                    </Button>
                                )}
                                <Eye className="w-4 h-4 text-muted-foreground" />
                            </div>
                        </div>

                        {/* Info shown directly on card */}
                        <div className="mt-3 pt-3 border-t flex items-start gap-6">
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-muted-foreground mb-1.5">Path</div>
                                <div className="text-xs font-medium truncate">{g.fullPath || g.path || "-"}</div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-muted-foreground mb-1.5">Parent</div>
                                <div className="text-xs font-medium">{g.parentId === null ? "-" : safeText(g.parentId)}</div>
                            </div>
                            <div className="flex-shrink-0 text-right">
                                <div className="text-xs text-muted-foreground mb-1.5">Instance</div>
                                <div className="text-xs font-medium truncate max-w-[200px]">{hostFromUrl(g.instanceUrl) || "-"}</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            );
        }

        return null;
    };

    return (
        <div className="h-full flex flex-col bg-background">
            <div className="p-6">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <FaGitlab className="w-8 h-8 text-primary" />
                    <div className="flex-1">
                        <h1 className="text-2xl font-semibold text-foreground">GitLab Data</h1>
                        <p className="text-sm text-muted-foreground">Synced entities for your configured tenant instance</p>
                    </div>
                    <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground hover:text-foreground" onClick={onBack}>
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </Button>
                </div>

                {/* Filters Row */}
                <div className="flex items-center gap-4 mb-6 flex-wrap">
                    {/* Tenant Dropdown */}
                    <div>
                        <label className="text-sm font-medium mb-2 block text-muted-foreground">Tenant</label>
                        <select
                            className="h-10 rounded-lg border border-border bg-background px-3 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors min-w-[180px]"
                            value={tenantName}
                            onChange={(e) => changeTenant(e.target.value)}
                            data-testid="select-tenant"
                        >
                            {tenants.map((t, idx) => {
                                const tn = extractTenantName(t);
                                return (
                                    <option key={`${tn}-${idx}`} value={tn}>{tn}</option>
                                );
                            })}
                        </select>
                    </div>

                    {/* Section Tabs */}
                    <div>
                        <label className="text-sm font-medium mb-2 block text-muted-foreground opacity-0 select-none">Section</label>
                        <div className="flex items-center gap-1 p-1 rounded-xl bg-muted/50 border border-border/30">
                            {SECTION_ORDER.map((sKey) => {
                                const sc = SECTION_CONFIG[sKey];
                                const SIcon = sc.icon;
                                const isActiveTab = activeSection === sKey;

                                return (
                                    <button
                                        key={sKey}
                                        onClick={() => setActiveSection(sKey)}
                                        className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all duration-200 ${isActiveTab
                                            ? "bg-background text-foreground shadow-sm border border-border/50"
                                            : "text-foreground hover:text-foreground hover:bg-background/50"
                                            }`}
                                    >
                                        <SIcon className="w-3.5 h-3.5" />
                                        {sc.label}
                                        <span
                                            className={`ml-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-semibold tabular-nums ${isActiveTab ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}
                                        >
                                            {counts[sKey]}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Search */}
                    <div className="flex-1 min-w-[200px]">
                        <label className="text-sm font-medium mb-2 block text-muted-foreground">Search</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder={`Search ${activeSection}...`}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                className="pl-10 pr-10"
                                data-testid="input-search-gitlab-data"
                            />
                            {query && (
                                <button
                                    onClick={() => setQuery("")}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Refresh Button */}
                    <div>
                        <label className="text-sm font-medium mb-2 block text-muted-foreground opacity-0 select-none">Actions</label>
                        <Button onClick={refresh} disabled={loading || !tenantName} className="gap-2">
                            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                            Refresh
                        </Button>
                    </div>
                </div>

                {/* Data List */}
                <div className="space-y-2">
                    {loading ? (
                        Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)
                    ) : rowsForSection().length === 0 ? (
                        <EmptyState section={activeSection} query={query} />
                    ) : (
                        rowsForSection().map((row, index) => renderRowCard(row, index))
                    )}
                </div>
            </div>
        </div>
    );
}
